type PosthogClient = typeof import('posthog-js').default;

type InitOptions = Parameters<PosthogClient['init']>[1];

let client: PosthogClient | null = null;
let clientPromise: Promise<PosthogClient | null> | null = null;
let initKey = '';
let initOptions: InitOptions | undefined;
let hasInitialized = false;

const browserWindow = () => (typeof window === 'undefined' ? null : window);

const afterStartup = (callback: () => void) => {
  const win = browserWindow();
  if (!win) return;
  const requestIdleCallback = (win as any).requestIdleCallback;
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, {timeout: 3000});
    return;
  }
  win.setTimeout(callback, 0);
};

const loadClient = async () => {
  if (client) return client;
  if (!clientPromise) {
    clientPromise = import('posthog-js')
      .then(module => module.default || (module as any))
      .catch(() => null);
  }
  client = await clientPromise;
  if (client && initKey && !hasInitialized) {
    client.init(initKey, initOptions);
    hasInitialized = true;
  }
  return client;
};

const withClient = (callback: (loadedClient: PosthogClient) => void) => {
  void loadClient().then(loadedClient => {
    if (!loadedClient) return;
    try {
      callback(loadedClient);
    } catch {
      // Analytics is best-effort; UI work should never depend on it.
    }
  });
};

export const analytics = {
  init(key: string | undefined, options?: InitOptions) {
    initKey = key || '';
    initOptions = options;
    if (!initKey) return;
    afterStartup(() => {
      void loadClient();
    });
  },

  capture(eventName: string, properties?: Record<string, any>) {
    withClient(loadedClient => {
      loadedClient.capture(eventName, properties);
    });
  },

  identify(distinctId: string, properties?: Record<string, any>) {
    withClient(loadedClient => {
      loadedClient.identify(distinctId, properties);
    });
  },

  reset() {
    withClient(loadedClient => {
      loadedClient.reset();
    });
  },

  hasOptedOutCapturing() {
    if (!client || typeof client.has_opted_out_capturing !== 'function')
      return false;
    return client.has_opted_out_capturing();
  },

  optInCapturing(options?: Record<string, any>) {
    withClient(loadedClient => {
      loadedClient.opt_in_capturing(options);
    });
  },

  optOutCapturing() {
    withClient(loadedClient => {
      loadedClient.opt_out_capturing();
    });
  },
};

export default analytics;
